import { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { PACKAGE_V1, PARTICIPANTS, PRODUCT_CLASSES } from "./data";
import { StatusChip, Kicker, ActionBtn } from "./ui";

export const ActPackage = () => {
    const [distributed, setDistributed] = useState(false);

    return (
        <section data-testid="act-package">
            <Kicker>Act 1 — Change published</Kicker>
            <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                The manufacturer publishes a bounded change package
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusChip tone="blue" testId="package-state-chip">CHANGE PUBLISHED</StatusChip>
                <StatusChip tone="amber">SYNTHETIC DATA</StatusChip>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
                <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="change-package-card">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-mono text-sm font-medium text-blue-950">
                            CHANGE PACKAGE {PACKAGE_V1.id}
                        </p>
                        <StatusChip tone="slate">Version {PACKAGE_V1.version}</StatusChip>
                    </div>
                    <dl className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                        <Row label="Affected product class" value={PACKAGE_V1.productClass} />
                        <Row label="Current product version" value={PACKAGE_V1.currentVersion} mono />
                        <Row label="Proposed version" value={PACKAGE_V1.proposedVersion} mono />
                        <Row label="Security classification" value={PACKAGE_V1.securityClassification} mono />
                        <Row label="Installation method" value={PACKAGE_V1.installMethod} />
                        <Row label="Rollback conditions" value={PACKAGE_V1.rollbackConditions} />
                        <Row label="Proposed release window" value={PACKAGE_V1.proposedWindow} mono />
                        <Row label="Supplier signature" value={PACKAGE_V1.supplierSignature} mono />
                        <Row label="Package reference" value={PACKAGE_V1.packageRef} mono />
                    </dl>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Change contents</p>
                            <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                {PACKAGE_V1.changeContents.map((c) => <li key={c}>· {c}</li>)}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Compatibility requirements</p>
                            <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                {PACKAGE_V1.compatibility.map((c) => <li key={c}>· {c}</li>)}
                            </ul>
                            <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">Post-installation tests</p>
                            <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                {PACKAGE_V1.postInstallTests.map((c) => <li key={c}>· {c}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="distribute-panel">
                        <p className="text-sm font-medium text-slate-900">Primary action</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">
                            Every participant receives the same authorised product change.
                            Each organisation evaluates it against the evidence and
                            responsibilities it controls locally.
                        </p>
                        <div className="mt-4">
                            <ActionBtn primary onClick={() => setDistributed(true)} testId="distribute-package-btn" disabled={distributed}>
                                <Send className="h-4 w-4" strokeWidth={1.5} />
                                {distributed ? "Package distributed" : "Distribute bounded change package"}
                            </ActionBtn>
                        </div>
                        <div className="mt-5 space-y-2" data-testid="distribute-targets">
                            {PARTICIPANTS.map((p, i) => (
                                <div key={p.id} className="flex items-center gap-3">
                                    <motion.span
                                        initial={false}
                                        animate={distributed ? { x: 0, opacity: 1 } : { x: -24, opacity: 0.25 }}
                                        transition={{ delay: distributed ? 0.25 * i : 0, duration: 0.4 }}
                                        className="grid h-5 w-5 place-items-center rounded-sm bg-blue-950 font-mono text-[9px] text-white"
                                    >
                                        {PACKAGE_V1.version}
                                    </motion.span>
                                    <span className="text-sm text-slate-700">{p.name}</span>
                                    {distributed && (
                                        <motion.span
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.25 * i + 0.3 }}
                                            className="ml-auto font-mono text-[10px] uppercase tracking-wide text-green-700"
                                            data-testid={`distributed-${p.id}`}
                                        >
                                            Received
                                        </motion.span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-sm border border-slate-200 bg-white p-6">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Replaceable product category</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">
                            The product class is configurable. The same journey can later represent:
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {PRODUCT_CLASSES.map((c) => (
                                <span key={c} className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

function Row({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2">
            <dt className="shrink-0 text-xs font-mono uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className={`text-right text-sm text-slate-800 ${mono ? "font-mono break-all" : ""}`}>{value}</dd>
        </div>
    );
}
